import React from 'react';
import { motion } from 'framer-motion';

import useAnimate from './Motion';

/**
 * Splits a headline into words that lift into focus one after another.
 * Words stay in normal flow so the line still wraps naturally.
 *
 * The in-view trigger lives on the heading, not on the words: each word starts
 * translated clear of its own overflow-hidden mask, so an observer watching the
 * word itself would never see enough of it to fire. Variants let the unclipped
 * parent do the observing and cascade the animation down.
 */
const word = {
    hidden: { y: '105%', opacity: 0, filter: 'blur(6px)' },
    show: {
        y: '0%',
        opacity: 1,
        filter: 'blur(0px)',
        transition: { duration: 1, ease: [0.16, 1, 0.3, 1] }
    }
};

export default function WordReveal({
    text,
    className,
    as: Tag = 'h1',
    delay = 0,
    stagger = 0.055,
    once = true
}) {
    const animate = useAnimate();
    const words = text.split(' ');

    /* The settled form is one text node, so the heading reads as a
       sentence to anything parsing the HTML. */
    if (!animate)
        return <Tag className={className}>{text}</Tag>;

    const Motion = motion[Tag] || motion.h1;

    return (
        <Motion
            className={className}
            aria-label={text}
            initial="hidden"
            whileInView="show"
            viewport={{ once, amount: 0.25 }}
            variants={{
                hidden: {},
                show: { transition: { staggerChildren: stagger, delayChildren: delay } }
            }}
        >
            {words.map((w, i) => (
                <span className="word-mask" key={`${w}-${i}`} aria-hidden="true">
                    <motion.span className="word-mask__inner" variants={word}>
                        {w}
                    </motion.span>
                </span>
            ))}
        </Motion>
    );
}
